import * as React from 'react';
import { useEffect, useState } from 'react';
import { makeStyles, Theme, createStyles, Typography } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import HourglassEmptyOutlinedIcon from '@material-ui/icons/HourglassEmptyOutlined';
import PersonAddDisabledIcon from '@material-ui/icons/PersonAddDisabled';
import RadioButtonUncheckedOutlinedIcon from '@material-ui/icons/RadioButtonUncheckedOutlined';
import PlayCircleOutlineOutlinedIcon from '@material-ui/icons/PlayCircleOutlineOutlined';
import {
    retrieveTaskStatusData,
    TaskStatus,
    TaskStatusEnum,
    TaskStatusQueryParams,
} from '../../../tasksSummary/TasksSummaryRepository';
import { SkeletonLoader } from '../../../components/loading/SkeletonLoader';
import { browserHistory } from '../../../components/AppContent';
import { linkToPage, showNotification } from '../../../App';
import EventSystem from '../../../EventSystem';
import { QUICK_VIEW_REFRESH_EVENT } from '../QuickViewDrawer';
import { RefreshingIndicator, runWithMinimumVisibleRefresh } from './RefreshingIndicator';

// Same mobile-app-ported palette YourEnquiriesWidget.tsx uses (accent re-tinted to blue,
// matching KpiSummaryWidget.tsx) - see that file's own comment on why this stays local to
// these two cards rather than joining --dc-*.
const MOBILE_ACCENT_TEAL = '#5173FF';
const MOBILE_ACCENT_TEAL_SOFT = '#5173FF24';
const MOBILE_STATUS_PENDING = '#D98324';
const MOBILE_STATUS_PENDING_SOFT = '#FCF0DC';
const MOBILE_STATUS_URGENT = '#E5484D';
const MOBILE_STATUS_URGENT_SOFT = '#FDE8E8';
const MOBILE_SURFACE_MUTED = '#F7F8FC';
const MOBILE_BORDER = '#E7E9F2';
const MOBILE_TEXT_SECONDARY = '#5B6579';
const MOBILE_TEXT_MUTED = '#9AA3B5';

const CHECK_PATH = 'M5,13 L10,18 L19,7';
// The path's own two-segment length (roughly sqrt(50) + sqrt(202) ~= 21.3), rounded up -
// see mobile's own AnimatedTaskCheckIcon comment (YourTasksOverview.tsx) for why.
const CHECK_PATH_LENGTH = 22;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // Same card construction as YourEnquiriesWidget.tsx/mobile's YourTasksOverview.tsx -
        // gradient wash + oversized watermark echo, header row.
        card: {
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#FFFFFF',
            // Matches the app's own standard card/table radius - see YourEnquiriesWidget.tsx's
            // identical comment.
            borderRadius: 8,
            padding: 20,
            // A stronger, layered "embossed" shadow instead of one faint blur - see
            // YourEnquiriesWidget.tsx's identical comment.
            boxShadow: '0 1px 3px rgba(16,26,51,0.12), 0 6px 16px rgba(16,26,51,0.08)',
        },
        wash: {
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${MOBILE_ACCENT_TEAL}29 0%, ${MOBILE_ACCENT_TEAL}05 100%)`,
            pointerEvents: 'none',
        },
        watermark: {
            position: 'absolute',
            top: -20,
            right: -22,
            color: MOBILE_ACCENT_TEAL,
            opacity: 0.08,
            pointerEvents: 'none',
        },
        content: {
            position: 'relative',
        },
        headerRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 12,
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
        },
        headerIconChip: {
            width: 38,
            height: 38,
            minWidth: 38,
            borderRadius: 14,
            backgroundColor: MOBILE_ACCENT_TEAL_SOFT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: '$iconBreathe 2200ms ease-in-out infinite alternate',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        '@keyframes iconBreathe': {
            from: { transform: 'scale(1)' },
            to: { transform: 'scale(1.12)' },
        },
        checkTrace: {
            strokeDasharray: CHECK_PATH_LENGTH,
            animation: '$checkDraw 1760ms ease-in-out infinite',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                strokeDashoffset: 0,
            },
        },
        '@keyframes checkDraw': {
            '0%': { strokeDashoffset: CHECK_PATH_LENGTH },
            '27%': { strokeDashoffset: 0 },
            '64%': { strokeDashoffset: 0 },
            '86%': { strokeDashoffset: CHECK_PATH_LENGTH },
            '100%': { strokeDashoffset: CHECK_PATH_LENGTH },
        },
        heading: {
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: -0.3,
            color: '#0B1220',
        },
        // "View all" - explicit request, matches YourTasksOverview.tsx's own header link
        // (this widget previously had no equivalent - the big stat number below is new too).
        viewAllLink: {
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            flexShrink: 0,
        },
        viewAllLabel: {
            fontSize: 12.5,
            fontWeight: 700,
            color: MOBILE_ACCENT_TEAL,
        },
        viewAllIcon: {
            fontSize: 14,
            color: MOBILE_ACCENT_TEAL,
            transform: 'rotate(-45deg)',
        },
        // The big "N open, across N statuses" stat - new, matches YourTasksOverview.tsx's own
        // statRow/statNumber/statLabel exactly (which is, in this widget's case, simply the
        // sum of the four metric cards below - unlike Enquiries, there's no separate query
        // needed, since these four statuses are the only ones this widget ever asks for).
        statRow: {
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
            marginBottom: 12,
        },
        statNumber: {
            fontSize: 30,
            fontWeight: 800,
            color: '#0B1220',
            letterSpacing: -0.5,
            lineHeight: 1,
        },
        statLabel: {
            fontSize: 14.5,
            fontWeight: 600,
            color: MOBILE_TEXT_SECONDARY,
        },
        statErrorText: {
            fontSize: 13,
            color: MOBILE_TEXT_SECONDARY,
            marginBottom: 12,
        },
        proportionBar: {
            display: 'flex',
            height: 8,
            borderRadius: 4,
            overflow: 'hidden',
            gap: 3,
            marginBottom: 12,
        },
        proportionSegment: {
            borderRadius: 4,
        },
        // Replaces the old wrapping pill-chip row with the boxed 2x2 metric-card grid mobile's
        // own card redesign uses - explicit request ("it has boxes inside where we show the
        // count"). CSS grid gives equal-width/equal-height cells outright, none of
        // YourTasksOverview.tsx's own JS-measured-width/height workaround is needed (that
        // exists purely for a React Native flex quirk with no web equivalent - see that
        // file's own metricCardWidth comment).
        metricCardsGrid: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
        },
        // Same "card sitting on a card" recipe YourEnquiriesWidget.tsx's own metric cards use -
        // see that file's own metricCard comment for why the card chrome itself stays a
        // uniformly muted background/border regardless of state, and for the hover recipe.
        metricCard: {
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: MOBILE_SURFACE_MUTED,
            border: `1px solid ${MOBILE_BORDER}`,
            borderRadius: 16,
            // Tighter than Enquiries' equivalent padding - a 2-up grid already gives each card
            // roughly 1.5x the width Enquiries' 3-up row does, matching mobile's own choice to
            // use a more compact card here (YourTasksOverview.tsx's own metricCard comment).
            padding: 10,
            transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease',
            '@media (prefers-reduced-motion: reduce)': {
                transform: 'none !important',
            },
        },
        metricCardClickable: {
            cursor: 'pointer',
            '&:hover': {
                boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
                borderColor: theme.palette.primary.light,
                transform: 'translateY(-1px)',
            },
        },
        metricCardCount: {
            fontSize: 19,
            fontWeight: 800,
            marginTop: 6,
        },
        metricCardLabel: {
            fontSize: 11,
            fontWeight: 600,
            color: MOBILE_TEXT_SECONDARY,
            marginTop: 2,
            lineHeight: '14px',
        },
        emptyState: {
            fontSize: 13,
            color: MOBILE_TEXT_SECONDARY,
        },
    })
);

// Same fixed four "not yet actioned" statuses, in the same order, as the mobile app's own
// YourTasksOverview.tsx - the rest (Completed/Closed etc.) aren't "things still to do" and
// belong on the full Tasks list, not this glance card.
const VISIBLE_STATUS_ORDER = ['queued', 'unassigned', 'not started', 'in progress'];

function normalizeStatusDescription(description: string): string {
    return description.trim().toLowerCase();
}

// Display text for a status the backend didn't return at all (as opposed to returning it
// with count: 0) - matches mobile's own STATUS_FALLBACK_LABELS, needed for the same reason:
// the 2x2 grid always renders all four slots (see the `statuses` build-up below), so a
// partial response needs a label to show on the synthetic zero-count card filling the gap.
const STATUS_FALLBACK_LABELS: Record<string, string> = {
    queued: 'Queued',
    unassigned: 'Unassigned',
    'not started': 'Not started',
    'in progress': 'In progress',
};

const CARD_META: Record<string, { icon: React.ReactNode; soft: string; strong: string }> = {
    queued: { icon: <HourglassEmptyOutlinedIcon style={{ fontSize: 16 }} />, soft: MOBILE_SURFACE_MUTED, strong: MOBILE_TEXT_SECONDARY },
    unassigned: { icon: <PersonAddDisabledIcon style={{ fontSize: 16 }} />, soft: MOBILE_STATUS_URGENT_SOFT, strong: MOBILE_STATUS_URGENT },
    'not started': { icon: <RadioButtonUncheckedOutlinedIcon style={{ fontSize: 16 }} />, soft: MOBILE_STATUS_PENDING_SOFT, strong: MOBILE_STATUS_PENDING },
    'in progress': { icon: <PlayCircleOutlineOutlinedIcon style={{ fontSize: 16 }} />, soft: MOBILE_ACCENT_TEAL_SOFT, strong: MOBILE_ACCENT_TEAL },
};

const TASK_STATUS_PARAMS: TaskStatusQueryParams = {
    taskStatusType: 'USER',
    includeCount: true,
    offset: null,
    first: null,
    filter: null,
    taskStatus: TaskStatusEnum.Open,
    isIncludeCrmTasks: false,
    isMyTasksOnly: true,
    groupBy: null,
    groupByValue: null,
    completedFromDate: null,
    completedToDate: null,
    updatedFromDate: null,
    updatedToDate: null,
    priorityList: null,
    statusList: null,
    allocatedToList: null,
    allocatedByList: null,
    created: null,
    due: null,
    teamList: null,
    taskTypes: null,
    isPersonalTask: false,
    isFromDataSource: true,
    matterList: null,
    isIncludeFollowUpTasksOnly: null,
    isIncludeCount: true,
};

// Same query TasksSummaryPage.tsx's own stat chips use (isMyTasksOnly: true), trimmed down
// to the same 4 statuses mobile's YourTasksOverview.tsx shows.
export const YourTasksWidget: React.FC = () => {
    const classes = useStyles();
    const [rawStatuses, setRawStatuses] = useState<TaskStatus[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    // Drives RefreshingIndicator - see YourEnquiriesWidget.tsx's identical comment for why
    // this is a separate flag from isLoading/hasError (which only the initial mount touches).
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Factored out of the mount effect below so Quick View's own header Refresh button
    // (QuickViewDrawer.tsx) can call the exact same fetch again rather than duplicating it -
    // deliberately doesn't touch isLoading/hasError on a refresh (only the initial mount
    // does), so a manual refresh swaps the card counts in place instead of flashing the
    // whole card back to its skeleton.
    const fetchStatuses = (onSuccess: (statuses: TaskStatus[]) => void, onSettled?: () => void) => {
        retrieveTaskStatusData(
            TASK_STATUS_PARAMS,
            true,
            (data) => {
                onSuccess(data.task.statuses);
                onSettled?.();
            },
            // tslint:disable-next-line: no-any
            (reason: any) => {
                setHasError(true);
                showNotification('Failed to Fetch Your Tasks', reason, 'error');
                onSettled?.();
            },
            // batchGroup opts this widget's query out of the default shared batch bucket -
            // see YourEnquiriesWidget.tsx's identical comment.
            { batchGroup: 'quickView-yourTasks' },
        );
    };

    useEffect(() => {
        fetchStatuses((statuses) => {
            setRawStatuses(statuses);
            setIsLoading(false);
        });
    // tslint:disable-next-line: align
    }, []);

    // Quick View's own header Refresh button - see YourEnquiriesWidget.tsx's own identical
    // comment for why fetchStatusesRef/the empty-deps effect/the explicit cleanup are each
    // needed (this widget's own fetchStatuses closure isn't referentially stable across
    // renders either, same reasoning as Apollo's refetch there).
    const fetchStatusesRef = React.useRef(fetchStatuses);
    fetchStatusesRef.current = fetchStatuses;
    useEffect(() => {
        // Explicit request: also refresh on the Task page's own task subscription (scoped
        // 'task' - see TasksSummaryPage.tsx's subscribeToTask), not just Quick View's header
        // Refresh button (which emits with no scope, i.e. "refresh everything" - only bail
        // out when a scope is given AND it isn't ours).
        const onRefresh = (scope?: string) => {
            if (scope && scope !== 'task') {
                return;
            }
            runWithMinimumVisibleRefresh(setIsRefreshing, () => new Promise<void>((resolve) => {
                fetchStatusesRef.current((statuses) => setRawStatuses(statuses), () => resolve());
            }));
        };
        EventSystem.on(QUICK_VIEW_REFRESH_EVENT, onRefresh);
        return () => { EventSystem.event.removeListener(QUICK_VIEW_REFRESH_EVENT, onRefresh); };
    // tslint:disable-next-line: align
    }, []);

    if (isLoading) {
        return (
            <div className={classes.card}>
                <SkeletonLoader variant="panel" columns={1} rows={4} />
            </div>
        );
    }

    if (hasError || !rawStatuses) {
        return null;
    }

    // Always exactly four entries, in VISIBLE_STATUS_ORDER, regardless of what the backend
    // actually returned - the 2x2 grid below has four fixed slots, matching mobile's own
    // YourTasksOverview.tsx exactly (a status the backend omits entirely gets a synthetic
    // zero-count entry rather than collapsing the grid to fewer cells).
    const statusByNormalizedDescription = new Map(
        rawStatuses
            .filter((status) => VISIBLE_STATUS_ORDER.includes(normalizeStatusDescription(status.description)))
            .map((status) => [normalizeStatusDescription(status.description), status] as const)
    );
    const statuses: TaskStatus[] = VISIBLE_STATUS_ORDER.map(
        (normalizedDescription) =>
            statusByNormalizedDescription.get(normalizedDescription) ?? {
                guidId: '',
                description: STATUS_FALLBACK_LABELS[normalizedDescription],
                sequence: 0,
                count: 0,
            }
    );
    const totalCount = statuses.reduce((runningTotal, status) => runningTotal + status.count, 0);
    const statLabel = totalCount > 0
        ? `open across ${statuses.length} status${statuses.length === 1 ? '' : 'es'}`
        : 'No open tasks assigned to you right now.';

    const onViewAllClick = () => {
        browserHistory.push(linkToPage('tasksSummary'));
    };

    return (
        <div className={classes.card}>
            <div className={classes.wash} />
            <svg width={112} height={112} viewBox="0 0 24 24" className={classes.watermark}>
                <path d={CHECK_PATH} stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
            <div className={classes.content}>
                <div className={classes.headerRow}>
                    <div className={classes.headerLeft}>
                        <div className={classes.headerIconChip}>
                            <svg width={24} height={24} viewBox="0 0 24 24">
                                <path
                                    className={classes.checkTrace}
                                    d={CHECK_PATH}
                                    stroke={MOBILE_ACCENT_TEAL}
                                    strokeWidth={2.5}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    fill="none"
                                />
                            </svg>
                        </div>
                        <Typography className={classes.heading}>Your Tasks</Typography>
                        {isRefreshing && <RefreshingIndicator />}
                    </div>
                    <button type="button" className={classes.viewAllLink} onClick={onViewAllClick}>
                        <Typography className={classes.viewAllLabel}>View all</Typography>
                        <ArrowForwardIcon className={classes.viewAllIcon} />
                    </button>
                </div>

                <div className={classes.statRow}>
                    <Typography className={classes.statNumber}>{totalCount.toLocaleString()}</Typography>
                    <Typography className={classes.statLabel}>{statLabel}</Typography>
                </div>

                {totalCount > 0 && (
                    <div className={classes.proportionBar}>
                        {statuses.map((status) => (
                            <div
                                key={normalizeStatusDescription(status.description)}
                                className={classes.proportionSegment}
                                style={{
                                    flexGrow: status.count,
                                    flexBasis: 0,
                                    backgroundColor: CARD_META[normalizeStatusDescription(status.description)].strong,
                                }}
                            />
                        ))}
                    </div>
                )}

                <div className={classes.metricCardsGrid}>
                    {statuses.map((status) => {
                        const normalizedDescription = normalizeStatusDescription(status.description);
                        const meta = CARD_META[normalizedDescription];
                        const isZero = status.count === 0;
                        const strong = isZero ? MOBILE_TEXT_MUTED : meta.strong;
                        const onClick = isZero ? undefined : onViewAllClick;
                        return (
                            <div
                                key={normalizedDescription}
                                className={`${classes.metricCard} ${onClick ? classes.metricCardClickable : ''}`}
                                onClick={onClick}
                                role={onClick ? 'button' : undefined}
                                tabIndex={onClick ? 0 : undefined}
                                onKeyDown={onClick ? (e) => { if (e.key === 'Enter') { onClick(); } } : undefined}
                            >
                                <span style={{ color: strong, display: 'flex' }}>{meta.icon}</span>
                                <Typography className={classes.metricCardCount} style={{ color: strong }}>{status.count.toLocaleString()}</Typography>
                                <Typography className={classes.metricCardLabel}>{status.description}</Typography>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
